<template>
  <div class="h-full flex flex-col bg-[#f8fafc] overflow-hidden min-w-0">
    <MenuListModal
      ref="menuListModalRef"
      :isOpen="showModal"
      :editMenu="editingMenu"
      @close="closeModal"
      @save="createMenu"
      @update="updateMenu"
    />

    <!-- Upload Progress Modal -->
    <div v-if="uploadStatus.uploading" class="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 text-center transform transition-all">
        <h3 class="text-lg font-bold text-[#0d4169] mb-4">{{ uploadStatus.text }}</h3>
        <div class="w-full bg-slate-200 rounded-full h-3 mb-2 overflow-hidden shadow-inner">
          <div 
            class="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
            :style="{ width: uploadStatus.progress + '%' }"
          ></div>
        </div>
        <div class="text-sm font-semibold text-slate-500">
          {{ uploadStatus.progress }}%
        </div>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-3 mb-4 shrink-0 min-w-0 w-full overflow-x-hidden md:flex md:flex-wrap md:items-center md:gap-3">
      <!-- Department filter -->
      <select
        v-model="selectedDepartmentId"
        @change="onDepartmentChange"
        class="w-full min-w-0 md:w-[200px] px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
      >
        <option value="">All Departments</option>
        <option v-for="dep in departments" :key="dep.id" :value="dep.id">
          {{ dep.name }}
        </option>
      </select>

      <!-- Sub Department filter -->
      <select
        v-if="selectedDepartmentId"
        v-model="selectedSubDepartmentId"
        class="w-full min-w-0 md:w-[200px] px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
      >
        <option value="">All Sub-Departments</option>
        <option v-for="sub in subDepartments" :key="sub.id" :value="sub.id">
          {{ sub.name }}
        </option>
      </select>

      <!-- Search -->
      <input
        v-model="searchQuery"
        type="text"
        class="w-full min-w-0 px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
        :class="selectedDepartmentId ? 'col-span-2 md:col-span-1 md:flex-1 md:min-w-0 md:max-w-[300px]' : 'col-span-1 md:flex-1 md:min-w-0 md:max-w-[300px]'"
        placeholder="Search Menu..."
      >

      <button
        type="button"
        @click="handleImport"
        :disabled="!canAddMenuList"
        class="w-full min-w-0 md:w-auto px-3 py-2 bg-slate-100 text-slate-700 text-[0.75rem] sm:text-[0.8rem] font-bold rounded-md transition-all flex items-center justify-center gap-1.5"
        :class="canAddMenuList ? 'hover:bg-slate-200' : 'opacity-50 cursor-not-allowed'"
      >
        <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
        Import (XSL)
      </button>
      <button
        type="button"
        @click="downloadSample"
        class="w-full min-w-0 md:w-auto px-3 py-2 bg-blue-50 text-blue-700 text-[0.75rem] sm:text-[0.8rem] font-bold rounded-md hover:bg-blue-100 transition-all flex items-center justify-center gap-1.5 border border-blue-100"
      >
        <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
        Sample Form
      </button>
    </div>

    <div class="w-full flex-1 min-h-0 overflow-x-auto overflow-y-auto">
      <table class="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
        <thead>
          <tr>
            <th>No.</th>
            <th class="sort-col" @click="sortBy('name')">
              Name
              <span v-if="sortKey === 'name'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th class="sort-col" @click="sortBy('department')">
              Department
              <span v-if="sortKey === 'department'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th class="sort-col" @click="sortBy('profit')">
             Profit
              <span v-if="sortKey === 'profit'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th class="sort-col" @click="sortBy('cost')">
              Cost
              <span v-if="sortKey === 'cost'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th class="sort-col" @click="sortBy('costPercentage')">
              Cost %
              <span v-if="sortKey === 'costPercentage'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th class="sort-col" @click="sortBy('price')">
              Original<br/>Price
              <span v-if="sortKey === 'price'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th class="sort-col" @click="sortBy('addition')">
              Addition
              <span v-if="sortKey === 'addition'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th class="sort-col" @click="sortBy('vat')">
              VAT
              <span v-if="sortKey === 'vat'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th class="sort-col" @click="sortBy('totalPrice')">
              Total Price
              <span v-if="sortKey === 'totalPrice'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th class="sort-col" @click="sortBy('isForStaff')">
              Is for staff
              <span v-if="sortKey === 'isForStaff'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th class="sort-col" @click="sortBy('isAvailable')">
              Available
              <span v-if="sortKey === 'isAvailable'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="!loading">
          <tr v-for="(menu, index) in filteredMenus" :key="menu.id" class="hover:bg-slate-100/50 transition-colors border-b">
            <td>{{ index + 1 }}</td>
            <td>{{ menu.name }}</td>
            <td>{{ menu.department?.name || '-' }}</td>
            <td>{{ (menu.price-menu.cost).toFixed(2) }}</td>
            <td>{{ formatMoney(menu.cost) }}</td>
            <td>{{ formatMoney(menu.costPercentage) }}%</td>
            <td>{{ formatMoney(menu.price) }}</td>
            <td>{{ formatMoney(menu.addition) }}</td>
            <td>{{ formatMoney(menu.vat) }}</td>
            <td>{{ formatMoney(menu.totalPrice || menu.total_price) }}</td>
            <td>
              <span
                class="px-2 py-0.5 rounded-full text-[10px] font-bold"
                :class="(menu.isForStaff ?? menu.is_for_staff) === 1 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'"
              >
                {{ (menu.isForStaff ?? menu.is_for_staff) === 1 ? 'Yes' : 'No' }}
              </span>
            </td>
            <td>
              <label class="toggle-switch" :class="{ 'opacity-50 cursor-not-allowed action-disabled-marker': !canToggleMenuAvailability }">
                <input
                  type="checkbox"
                  :checked="(menu.isAvailable ?? 1) === 1"
                  :disabled="!canToggleMenuAvailability"
                  @change="toggleAvailability(menu)"
                >
                <span class="slider"></span>
              </label>
            </td>
            <td class="actions">
              <button
                @click="editMenuItem(menu)"
                :class="canEditMenuList ? '' : 'opacity-50 cursor-not-allowed action-disabled-marker'"
                :disabled="!canEditMenuList"
              >
                Edit
              </button>
              <label class="toggle-switch" title="Status" :class="{ 'opacity-50 cursor-not-allowed action-disabled-marker': !canEditMenuList }">
                <input
                  type="checkbox"
                  :checked="(menu.status ?? 1) === 1"
                  :disabled="!canEditMenuList"
                  @change="toggleStatus(menu)"
                >
                <span class="slider"></span>
              </label>
            </td>
          </tr>
          </template>
          <tr v-if="loading">
            <td colspan="13" class="text-center py-10">
              <div class="flex items-center justify-center">
                <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
              </div>
            </td>
          </tr>
          <tr v-else-if="filteredMenus.length === 0">
            <td colspan="13" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
              No menu registered yet.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, reactive } from 'vue'
import { apiFetch, API_BASE_URL, WS_BASE_URL, normalizeUploadValue } from '../../utils/api'
import store from '../../store'
import MenuListModal from './MenuListModal.vue'

const menus = ref([])
const departments = ref([])
const subDepartments = ref([])
const loading = ref(false)
const searchQuery = ref('')
const selectedDepartmentId = ref('')
const selectedSubDepartmentId = ref('')
const showModal = ref(false)
const editingMenu = ref(null)
const sortKey = ref('name')
const sortOrder = ref('asc')
const menuListModalRef = ref(null)

let menuListSyncSocket = null
let menuListSyncReconnectTimer = null

const uploadStatus = reactive({
  uploading: false,
  progress: 0,
  text: 'Uploading file...'
})

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)
const userId = computed(() => currentUser.value?.id || null)
const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string'
    ? JSON.parse(currentUser.value.privilege)
    : currentUser.value.privilege
})
const hasAdminPrivilege = computed(() => userPrivileges.value?.All === true || userPrivileges.value?.all === true)
const menuListPrivilege = computed(() => {
  const raw = userPrivileges.value?.['Menu List'] || userPrivileges.value?.['menu list'] || null
  if (!raw) return null
  return {
    ...raw,
    Available: !!(raw.Available ?? raw.Void),
  }
})
const canAddMenuList = computed(() => hasAdminPrivilege.value || !!menuListPrivilege.value?.Add)
const canEditMenuList = computed(() => hasAdminPrivilege.value || !!menuListPrivilege.value?.Edit)
const canToggleMenuAvailability = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!menuListPrivilege.value?.Available
})

function formatMoney(val) {
  const num = Number(val || 0)
  return num.toFixed(2)
}

async function loadDepartments() {
  if (!companyId.value && !branchId.value) return
  try {
    const params = new URLSearchParams()
    if (branchId.value) params.append('branchId', branchId.value)
    else if (companyId.value) params.append('companyId', companyId.value)
    const response = await apiFetch(`/departments?${params.toString()}`, { method: 'GET' })
    if (!response.ok) return
    const data = await response.json().catch(() => [])
    departments.value = Array.isArray(data) ? data : []
    departments.value.sort((a, b) => a.name.localeCompare(b.name))
  } catch (err) {
    console.error('Error loading departments:', err)
  }
}

async function loadSubDepartments(depId) {
  try {
    subDepartments.value = []
    if (!depId) return
    const response = await apiFetch(`/sub-departments/department/${depId}`, { method: 'GET' })
    if (!response.ok) return
    const data = await response.json().catch(() => [])
    subDepartments.value = Array.isArray(data) ? data : []
    subDepartments.value.sort((a, b) => a.name.localeCompare(b.name))
  } catch (err) {
    console.error('Error loading sub-departments:', err)
  }
}

function onDepartmentChange() {
  selectedSubDepartmentId.value = ''
  if (selectedDepartmentId.value) {
    loadSubDepartments(selectedDepartmentId.value)
  } else {
    subDepartments.value = []
  }
}

async function loadMenus() {
  if (!companyId.value && !branchId.value) return
  loading.value = true
  try {
    const params = new URLSearchParams()
    if (branchId.value) params.append('branchId', branchId.value)
    else if (companyId.value) params.append('companyId', companyId.value)
    const response = await apiFetch(`/menu-lists?${params.toString()}`, { method: 'GET' })
    if (!response.ok) {
      console.error('Failed to load menu list')
      return
    }
    const data = await response.json().catch(() => [])
    menus.value = Array.isArray(data) ? data : []
  } catch (err) {
    console.error('Error loading menu list:', err)
  } finally {
    loading.value = false
  }
}

const filteredMenus = computed(() => {
  let result = [...menus.value]

  // Filter by selected department
  if (selectedDepartmentId.value) {
    result = result.filter(m => {
      const depId = m.departmentId ?? m.department_id ?? m.department?.id
      return String(depId) === String(selectedDepartmentId.value)
    })
  }

  // Filter by selected sub-department
  if (selectedSubDepartmentId.value) {
    result = result.filter(m => {
      const subDepId = m.subDepartmentId ?? m.sub_department_id ?? m.subDepartment?.id
      return String(subDepId) === String(selectedSubDepartmentId.value)
    })
  }

  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    result = result.filter(m => (m.name || '').toLowerCase().includes(q))
  }

  result.sort((a, b) => {
    const key = sortKey.value
    const order = sortOrder.value === 'asc' ? 1 : -1

    const getVal = (item) => {
      switch (key) {
        case 'name':
          return (item.name || '').toString().toLowerCase()
        case 'department':
          return (item.department?.name || '').toString().toLowerCase()
        case 'profit':
          return Number(item.price || 0) - Number(item.cost || 0)
        case 'subDepartment':
          return (item.subDepartment?.name || '').toString().toLowerCase()
        case 'price':
          return Number(item.price || 0)
        case 'cost':
          return Number(item.cost || 0)
        case 'costPercentage':
          return Number(item.costPercentage || 0)
        case 'addition':
          return Number(item.addition || 0)
        case 'vat':
          return Number(item.vat || 0)
        case 'totalPrice':
          return Number(item.totalPrice ?? item.total_price ?? 0)
        case 'isForStaff':
          return Number(item.isForStaff ?? item.is_for_staff ?? 0)
        case 'isAvailable':
          return Number(item.isAvailable ?? item.is_available ?? 1)
        case 'status':
          return Number(item.status ?? 1)
        default:
          return 0
      }
    }

    const va = getVal(a)
    const vb = getVal(b)

    if (va < vb) return -1 * order
    if (va > vb) return 1 * order
    return 0
  })

  return result
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

function openAddModal() {
  if (!canAddMenuList.value) return
  editingMenu.value = null
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  editingMenu.value = null
}

function editMenuItem(menu) {
  if (!canEditMenuList.value) return
  editingMenu.value = menu
  showModal.value = true
}

function handleImport() {
  if (!canAddMenuList.value) return
  if (!selectedDepartmentId.value) {
    alert('Please select a department before importing.');
    return;
  }

  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.xlsx, .csv';
  input.onchange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('companyId', companyId.value || '');
    formData.append('branchId', branchId.value || '');
    formData.append('created_by', userId.value || '');
    formData.append('departmentId', selectedDepartmentId.value);
    formData.append('subDepartmentId', selectedSubDepartmentId.value || '');

    uploadStatus.uploading = true;
    uploadStatus.progress = 0;
    uploadStatus.text = 'Uploading file...';

    const xhr = new XMLHttpRequest();
    const url = `${API_BASE_URL}/menu-lists/import`;

    xhr.open('POST', url, true);
    
    // Set Authorization header
    const token = store.state.token || localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    // Progress handler
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        uploadStatus.progress = percentComplete;
        if (percentComplete === 100) {
          uploadStatus.text = 'Processing data...';
        }
      }
    };

    // Load handler
    xhr.onload = async () => {
      uploadStatus.uploading = false;
      if (xhr.status >= 200 && xhr.status < 300) {
        let result = {};
        try { 
          result = JSON.parse(xhr.responseText); 
        } catch (e) { 
          console.error('Failed to parse successful response:', e);
        }
        alert(result.message || 'Import successful');
        await loadMenus();
      } else {
        let result = {};
        try { 
          result = JSON.parse(xhr.responseText); 
        } catch (e) { 
          console.error('Failed to parse error response:', e);
        }
        alert(result.message || 'Error importing menu items');
      }
    };

    // Error handler
    xhr.onerror = () => {
      uploadStatus.uploading = false;
      alert('Network error during upload');
    };

    xhr.send(formData);
  };
  input.click();
}

function downloadSample() {
  // Download static template from client public folder
  const link = document.createElement('a')
  link.href = '/menu.xlsx'
  link.setAttribute('download', 'menu.xlsx')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

async function createMenu(payload) {
  try {
    const response = await apiFetch('/menu-lists', {
      method: 'POST',
      body: JSON.stringify({
        name: payload.name,
        departmentId: payload.departmentId,
        subDepartmentId: payload.subDepartmentId,
        totalPrice: payload.totalPrice,
        price: payload.price,
        addition: payload.addition,
        vat: payload.vat,
        isTaxable: payload.isTaxable,
        isForStaff: payload.isForStaff,
        companyId: payload.branchId ? null : payload.companyId,
        branchId: payload.branchId,
        created_by: payload.userId || userId.value,
        packagingFee: payload.packagingFee,
        image: normalizeUploadValue(payload.image),
      }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      menuListModalRef.value?.setError(err.message || 'Failed to save menu')
      return
    }
    closeModal()
    await loadMenus()
  } catch (err) {
    console.error('Error saving menu:', err)
    menuListModalRef.value?.setError('An unexpected error occurred.')
  }
}

async function updateMenu(payload) {
  if (!payload.id) return
  try {
    const response = await apiFetch(`/menu-lists/${payload.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        name: payload.name,
        departmentId: payload.departmentId,
        subDepartmentId: payload.subDepartmentId,
        totalPrice: payload.totalPrice,
        price: payload.price,
        addition: payload.addition,
        vat: payload.vat,
        isTaxable: payload.isTaxable,
        isForStaff: payload.isForStaff,
        packagingFee: payload.packagingFee,
        image: normalizeUploadValue(payload.image),
      }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      menuListModalRef.value?.setError(err.message || 'Failed to update menu')
      return
    }
    closeModal()
    await loadMenus()
  } catch (err) {
    console.error('Error updating menu:', err)
    menuListModalRef.value?.setError('An unexpected error occurred.')
  }
}

async function toggleAvailability(menu) {
  if (!canToggleMenuAvailability.value) return
  try {
    const newVal = (menu.isAvailable ?? 1) === 1 ? 0 : 1
    const response = await apiFetch(`/menu-lists/${menu.id}`, {
      method: 'PUT',
      body: JSON.stringify({ isAvailable: newVal }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      alert(err.message || 'Failed to update availability')
      return
    }
    await loadMenus()
  } catch (err) {
    console.error('Error toggling menu availability:', err)
  }
}

async function toggleStatus(menu) {
  if (!canEditMenuList.value) return
  try {
    const newStatus = (menu.status ?? 1) === 1 ? 0 : 1
    const response = await apiFetch(`/menu-lists/${menu.id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: newStatus }),
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      alert(err.message || 'Failed to update status')
      return
    }
    await loadMenus()
  } catch (err) {
    console.error('Error toggling menu status:', err)
  }
}

function disconnectMenuListSyncRealtime() {
  if (menuListSyncReconnectTimer) {
    clearTimeout(menuListSyncReconnectTimer)
    menuListSyncReconnectTimer = null
  }
  if (menuListSyncSocket) {
    try {
      menuListSyncSocket.close()
    } catch (_) {
      // ignore close failures
    }
    menuListSyncSocket = null
  }
}

function connectMenuListSyncRealtime() {
  disconnectMenuListSyncRealtime()

  const user = currentUser.value
  if (!user || typeof WebSocket === 'undefined') return

  const scopedCompanyId = user.companyId || user.company_id || user.company?.id
  if (!scopedCompanyId) return

  const scopedBranchId = user.branchId || user.branch_id || user.branch?.id || null
  const socket = new WebSocket(WS_BASE_URL)
  menuListSyncSocket = socket

  socket.addEventListener('open', () => {
    socket.send(JSON.stringify({
      type: 'frontend.subscribe',
      company_id: scopedCompanyId,
      branch_id: scopedBranchId,
    }))
  })

  socket.addEventListener('message', (event) => {
    try {
      const message = JSON.parse(event.data)
      if (message.event?.startsWith('menu_list.')) {
        loadMenus()
      }
    } catch (_) {
      // ignore malformed realtime payloads
    }
  })

  socket.addEventListener('close', () => {
    menuListSyncSocket = null
    menuListSyncReconnectTimer = setTimeout(() => {
      connectMenuListSyncRealtime()
    }, 3000)
  })
}

onMounted(() => {
  loadDepartments()
  loadMenus()
  connectMenuListSyncRealtime()
  window.addEventListener('open-menu-list-modal', openAddModal)
})

onUnmounted(() => {
  disconnectMenuListSyncRealtime()
  window.removeEventListener('open-menu-list-modal', openAddModal)
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  color: #1e293b;
}

th,
td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  font-size: 0.7rem;
}

th {
  background: #f1f5f9;
  font-weight: 600;
}
th.sort-col {
  cursor: pointer;
}
th.sort-col:hover {
  background: #e2e8f0;
}

tbody tr:hover {
  background: #f1f5f9;
}

.status-pill {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: bold;
}
.active  { background: #dcfce7; color: #166534; }
.inactive { background: #fee2e2; color: #991b1b; }

.actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.actions button {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 34px;
  transition: 0.2s;
}
.slider::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  left: 2px;
  top: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}
.toggle-switch input:checked + .slider {
  background: #22c55e;
}
.toggle-switch input:not(:checked) + .slider {
  background: #ef4444;
}
.toggle-switch input:checked + .slider::before {
  transform: translateX(13px);
}

.action-disabled-marker {
  position: relative;
}

.action-disabled-marker:hover::after {
  content: '⊘';
  position: absolute;
  top: -8px;
  right: -10px;
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
  line-height: 1;
  pointer-events: none;
}
</style>
